// Bridge to the PassCall iOS and Android apps.
//
// The iOS app is a native shell (sign-in, friends, settings screens written in
// SwiftUI) around this web app, which it shows only for the window itself. The
// shell talks to the page through WebKit's script message handler "passcall":
//
//   shell -> page   PassCallNative.call(id, method, args)   (evaluateJavaScript)
//   page  -> shell  window.webkit.messageHandlers.passcall.postMessage({event, ...})
//
// Every call answers with a {event:"result", id, ok, value|error} message. The
// page also pushes "auth", "prefs", "mode", "camera" and "window" events whenever
// those change, so the shell never polls.
//
// Nothing here runs in an ordinary browser: `isNative` is false unless the
// WebKit handler exists, and App.svelte then behaves exactly as before.
import { sendVerificationEmail, sendPasswordResetEmail } from "./verification.js";
import { get } from "svelte/store";
import { writable } from "svelte/store";
import { callActive, cameraActive } from "./callState.js";
import { lookupByEmail, ensureDirectoryEntry } from "./directory.js";
import { ensureProfile, getDisplayNames } from "./profiles.js";
import { ensureInviteCode, inviteLink, inviteMessage } from "./invites.js";
import { newUserDocument } from "./prefsDefaults.js";

// Two shells speak this protocol: the iOS app through WebKit's "passcall" message
// handler, and the Android app through a JavaScript interface named PassCallAndroid
// (addJavascriptInterface), which takes the message as a JSON string.
const iosHandler = () =>
  typeof window !== "undefined" && window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.passcall;
const androidHandler = () =>
  typeof window !== "undefined" && window.PassCallAndroid && typeof window.PassCallAndroid.postMessage === "function";
export const isNative = !!(iosHandler() || androidHandler());
export const nativePlatform = iosHandler() ? "ios" : androidHandler() ? "android" : "";

// What the shell wants the page to show: "idle" (blank, camera off, offline) or
// "window" (the PassCall window with the detector running).
export const shellMode = writable("idle");

function post(message) {
  if (!isNative) return;
  try {
    if (androidHandler()) window.PassCallAndroid.postMessage(JSON.stringify(message));
    else window.webkit.messageHandlers.passcall.postMessage(message);
  } catch (e) {
    console.warn("native bridge: post failed", e);
  }
}

const AUTH_ERRORS = {
  "auth/email-already-in-use": "An account already exists for that email address. Sign in instead, or reset your password.",
  "auth/invalid-email": "That email address doesn't look right.",
  "auth/weak-password": "Please choose a stronger password: at least 8 characters, with a capital letter, a number and a symbol.",
  "auth/user-not-found": "No account was found for that email address.",
  "auth/wrong-password": "That password is incorrect.",
  "auth/invalid-credential": "That email address or password is incorrect.",
  "auth/invalid-login-credentials": "That email address or password is incorrect.",
  "auth/user-disabled": "This account has been disabled. Contact help@passcall.io.",
  "auth/too-many-requests": "Too many attempts. Please wait a few minutes and try again.",
  "auth/network-request-failed": "Network error. Check your connection and try again.",
  "auth/requires-recent-login": "For your security, please sign in again before doing that.",
};

export function passwordRequirements(error) {
  // Firebase: "Missing password requirements: [Password must contain a lower case
  // character, Password must contain a numeric character]" -> one readable sentence.
  const m = String((error && error.message) || "").match(/\[(.*)\]/);
  if (!m) return "Please choose a stronger password (at least 8 characters).";
  const parts = m[1].split(/,\s*(?=Password)/).map((t) => t.replace(/^Password must /, "").trim()).filter(Boolean);
  return "Your password must " + parts.join(", ") + ".";
}

function describeError(error) {
  const code = error && error.code;
  if (code === "auth/password-does-not-meet-requirements") return passwordRequirements(error);
  if (code && AUTH_ERRORS[code]) return AUTH_ERRORS[code];
  return (error && error.message) || "Something went wrong. Please try again.";
}

function userInfo(user) {
  if (!user) return null;
  return {
    uid: user.uid,
    email: user.email || "",
    name: user.displayName || "",
    emailVerified: !!user.emailVerified,
  };
}

let ctx = null; // { firebase, auth, db, getPrefs, applyPrefs, reloadPrefs, validatePrefs }

/**
 * Called once by App.svelte when running inside the iOS shell. `getPrefs` returns
 * the live prefs object, `applyPrefs(partial)` merges values into it, and
 * `reloadPrefs()` re-reads Users/{uid} and returns whether it validated.
 */
export function nativeInit(options) {
  if (!isNative) return;
  ctx = options;
  cameraActive.subscribe((on) => post({ event: "camera", on: !!on }));
  callActive.subscribe((open) => post({ event: "window", open: !!open }));
  shellMode.subscribe((mode) => post({ event: "mode", mode }));
  window.PassCallNative = { call };
  post({ event: "ready", version: options.version || "" });
}

/** App.svelte calls this whenever the signed-in account changes. */
export function nativeNotifyAuth(user) {
  post({ event: "auth", user: userInfo(user) });
}

/** App.svelte calls this after prefs are (re)loaded from Firestore. */
export function nativeNotifyPrefs(prefs, valid) {
  post({ event: "prefs", prefs: { ...prefs }, valid: !!valid });
}

// ---- Methods the shell can call ------------------------------------------------

async function signIn({ email, password }) {
  await ctx.auth.signInWithEmailAndPassword(String(email || "").trim(), String(password || ""));
  return userInfo(ctx.auth.currentUser);
}

async function signUp({ name, email, password }) {
  name = String(name || "").trim();
  email = String(email || "").trim();
  password = String(password || "");
  if (!name) throw new Error("Please enter your name.");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error("That email address doesn't look right.");
  if (password.length < 8) throw new Error("Password must be at least 8 characters, with a capital letter, a number and a symbol.");
  const credential = await ctx.auth.createUserWithEmailAndPassword(email, password);
  const user = credential.user;
  await user.updateProfile({ displayName: name });
  await sendVerificationEmail(user).catch((e) => console.warn("Verification email not sent", e));
  await ctx.db.collection("Users").doc(user.uid).set(
    newUserDocument({ name, email: user.email || email, createdAt: ctx.firebase.firestore.FieldValue.serverTimestamp() })
  );
  await ensureDirectoryEntry(ctx.db, user, name).catch((e) => console.warn("Directory entry not written", e));
  await ensureProfile(ctx.db, user, name).catch((e) => console.warn("Profile not written", e));
  return userInfo(user);
}

async function resetPassword({ email }) {
  try {
    await sendPasswordResetEmail(ctx.auth, String(email || "").trim());
  } catch (e) {
    if (!(e && e.code === "auth/user-not-found")) throw e; // never reveal registered addresses
  }
  return { sent: true };
}

async function signOut() {
  if (ctx.goOffline) await ctx.goOffline();
  shellMode.set("idle");
  await ctx.auth.signOut();
  return { ok: true };
}

async function resendVerification() {
  const user = ctx.auth.currentUser;
  if (!user) throw new Error("Not signed in.");
  await sendVerificationEmail(user);
  return { sent: true };
}

async function refreshVerification() {
  const user = ctx.auth.currentUser;
  if (!user) return null;
  await user.reload().catch(() => {});
  const info = userInfo(ctx.auth.currentUser);
  if (ctx.onVerificationChanged) ctx.onVerificationChanged(info.emailVerified);
  nativeNotifyAuth(ctx.auth.currentUser);
  return info;
}

async function deleteAccount({ password }) {
  const current = ctx.auth.currentUser;
  if (!current) throw new Error("Not signed in.");
  shellMode.set("idle");
  try {
    await current.delete();
  } catch (e) {
    if (e && e.code === "auth/requires-recent-login" && password) {
      const credential = ctx.firebase.auth.EmailAuthProvider.credential(current.email, password);
      await current.reauthenticateWithCredential(credential);
      await current.delete();
    } else {
      throw e;
    }
  }
  return { deleted: true };
}

function state() {
  const user = ctx.auth.currentUser;
  return {
    user: userInfo(user),
    prefs: { ...ctx.getPrefs() },
    mode: get(shellMode),
    camera: get(cameraActive),
    window: get(callActive),
  };
}

async function friends() {
  const user = ctx.auth.currentUser;
  if (!user) throw new Error("Not signed in.");
  const [userDoc, friendshipDoc] = await Promise.all([
    ctx.db.collection("Users").doc(user.uid).get(),
    ctx.db.collection("Friendships").doc(user.uid).get().catch(() => null),
  ]);
  const data = userDoc.exists ? userDoc.data() : {};
  const ids = Array.isArray(data.friends) ? data.friends.filter((id) => typeof id === "string" && id) : [];
  const f = friendshipDoc && friendshipDoc.exists ? friendshipDoc.data() : null;
  const paused = f && Array.isArray(f.paused) ? f.paused : [];
  const names = await getDisplayNames(ctx.db, ids);
  return ids.map((id) => ({ id, name: names[id] || "", paused: paused.includes(id) }));
}

async function lookupFriend({ email }) {
  const user = ctx.auth.currentUser;
  email = String(email || "").trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error("That doesn't look like an email address.");
  if (user && user.email && email.toLowerCase() === user.email.toLowerCase()) throw new Error("That's you. Add someone else.");
  const found = await lookupByEmail(ctx.db, email);
  if (!found) return { found: false, email };
  return { found: true, id: found.id, name: found.name || "", email };
}

async function saveFriends({ ids, paused }) {
  const user = ctx.auth.currentUser;
  if (!user) throw new Error("Not signed in.");
  ids = Array.isArray(ids) ? ids.filter((id) => typeof id === "string" && id) : [];
  paused = Array.isArray(paused) ? paused.filter((id) => ids.includes(id)) : [];
  await ctx.db.collection("Users").doc(user.uid).update({ friends: ids });
  await ctx.db.collection("Friendships").doc(user.uid).set({ approved: ids, paused }, { merge: true });
  return { saved: true, count: ids.length };
}

/** The account's invite link and the message to send with it; `fresh` retires the old link. */
async function invite({ fresh } = {}) {
  const user = ctx.auth.currentUser;
  if (!user) throw new Error("Not signed in.");
  const name = ctx.getPrefs().name || user.displayName || "";
  const code = await ensureInviteCode(ctx.db, ctx.firebase, user, name, { fresh: !!fresh });
  return { code, link: inviteLink(code), text: inviteMessage(code, name) };
}

async function savePrefs({ prefs }) {
  const user = ctx.auth.currentUser;
  if (!user) throw new Error("Not signed in.");
  const partial = prefs && typeof prefs === "object" ? prefs : {};
  const merged = { ...ctx.getPrefs(), ...partial };
  const result = ctx.validatePrefs(merged);
  const update = { ...result.validatedData, screenBackground: ctx.firebase.firestore.FieldValue.delete() };
  await ctx.db.collection("Users").doc(user.uid).update(update);
  ensureProfile(ctx.db, user, result.validatedData.name).catch((e) => console.warn("Profile not updated", e));
  ctx.applyPrefs(result.validatedData);
  nativeNotifyPrefs(ctx.getPrefs(), result.isValid);
  // The detector and the call read prefs when they start, so a change while the
  // window is open takes effect by restarting the window.
  if (get(shellMode) === "window") {
    shellMode.set("idle");
    setTimeout(() => shellMode.set("window"), 300);
  }
  return { saved: true, valid: result.isValid };
}

async function devices() {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return { video: [], audio: [] };
  const list = await navigator.mediaDevices.enumerateDevices();
  let cameras = 0, mics = 0;
  const video = [], audio = [];
  list.forEach((d) => {
    if (d.kind === "videoinput") video.push({ id: d.deviceId, label: d.label || `Camera ${++cameras}` });
    else if (d.kind === "audioinput") audio.push({ id: d.deviceId, label: d.label || `Microphone ${++mics}` });
  });
  return { video, audio };
}

function setMode({ mode }) {
  shellMode.set(mode === "window" ? "window" : "idle");
  return { mode: get(shellMode) };
}

const methods = {
  signIn, signUp, resetPassword, signOut, resendVerification, refreshVerification, deleteAccount,
  state, friends, lookupFriend, saveFriends, invite, savePrefs, devices, setMode,
};

async function call(id, method, args) {
  const fn = methods[method];
  if (!ctx || !fn) {
    post({ event: "result", id, ok: false, error: "Unknown method " + method });
    return;
  }
  try {
    const value = await fn(args || {});
    post({ event: "result", id, ok: true, value: value === undefined ? null : value });
  } catch (error) {
    console.warn("native bridge:", method, error);
    post({ event: "result", id, ok: false, error: describeError(error), code: (error && error.code) || "" });
  }
}

/** App.svelte calls this when the page asks for a screen the shell owns. */
export function nativeOpen(screen) {
  post({ event: "open", screen });
}
